import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Filter, Calendar, TrendingUp, Package, CheckCircle, XCircle, Clock, BarChart3, PieChart, FileText, Download } from 'lucide-react';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, processClientRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { exportGroupedExcel } from '../../utils/excelExport';
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const DELIVERY_NOTE_COLUMNS = [
  { field: 'refno', title: 'Ref No', widthClass: 'w-[7%]', hideable: true },
  { field: 'date', title: 'Date', widthClass: 'w-[7%]', hideable: true },
  { field: 'drivername', title: 'Driver', widthClass: 'w-[9%]', hideable: true },
  { field: 'vehiclenumber', title: 'Vehicle', widthClass: 'w-[9%]', hideable: true },
  { field: 'totalorders', title: 'Total Orders', widthClass: 'w-[7%]', hideable: true },
  { field: 'pending', title: 'Pending', widthClass: 'w-[7%]', hideable: true },
  { field: 'delivered', title: 'Delivered', widthClass: 'w-[7%]', hideable: true },
  { field: 'returned', title: 'Returned', widthClass: 'w-[7%]', hideable: true },
  { field: 'cancelled', title: 'Cancelled', widthClass: 'w-[7%]', hideable: true },
  { field: 'collectedamount', title: 'Collected Amount', widthClass: 'w-[9%]', hideable: true },
  { field: 'pendingcollection', title: 'Pending Collection', widthClass: 'w-[9%]', hideable: true },
  { field: 'status', title: 'Status', widthClass: 'w-[7%]', hideable: true },
];

const defaultNoteVisibility = () =>
  DELIVERY_NOTE_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const formatNoteDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatVehicleDisplay = (note) => {
  if (!note?.Vehicle) return '-';
  const { Number: num, Model: model } = note.Vehicle;
  return model ? `${num} (${model})` : num || '-';
};

const DeliveryStatistics = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Statistics data (will be populated from API)
  const [statistics, setStatistics] = useState({
    totalDeliveryNotes: 0,
    confirmed: 0,
    delivered: 0,
    returned: 0,
    cancelled: 0,
    processing: 0,
    totalOrders: 0,
    collectedAmount: 0,
    pendingCollection: 0
  });

  // Current delivery notes with detailed statistics
  const [currentDeliveryNotes, setCurrentDeliveryNotes] = useState([]);

  // Date filters
  const [dateRange, setDateRange] = useState({
    fromDate: '',
    toDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultNoteVisibility);
  const [groupField, setGroupField] = useState(null);

  // Status distribution (pie) data
  const statusData = useMemo(() => {
    return [
      { name: 'Delivered', value: statistics.delivered || 0, color: '#22c55e' },
      { name: 'Processing', value: statistics.processing || 0, color: '#a855f7' },
      { name: 'Returned', value: statistics.returned || 0, color: '#f97316' },
      { name: 'Cancelled', value: statistics.cancelled || 0, color: '#ef4444' },
    ].filter((item) => item.value > 0);
  }, [statistics]);

  // Delivery trend (bar) data - orders grouped by note date
  const trendData = useMemo(() => {
    const map = new Map();
    (currentDeliveryNotes || []).forEach((note) => {
      if (!note.Date) return;
      const d = new Date(note.Date);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      const sortKey = d.getTime();
      const existing = map.get(key) || { date: key, orders: 0, sortKey };
      existing.orders += note.TotalOrders || 0;
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey);
  }, [currentDeliveryNotes]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await MainService.DeliveryStatistics({
        FromDateString: dateRange.fromDate || '',
        ToDateString: dateRange.toDate || '',
      });

      const summary = response?.summary || {};
      setStatistics({
        totalDeliveryNotes: summary.totalDeliveryNotes || 0,
        confirmed: summary.confirmed || 0,
        delivered: summary.delivered || 0,
        returned: summary.returned || 0,
        cancelled: summary.cancelled || 0,
        processing: summary.processing || 0,
        totalOrders: summary.totalOrders || 0,
        collectedAmount: summary.collectedAmount || 0,
        pendingCollection: summary.pendingCollection || 0
      });
      setCurrentDeliveryNotes(response?.currentDeliveryNotes || []);
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  const handleApplyFilters = () => {
    loadStatistics();
  };

  const handleClearFilters = () => {
    setDateRange({
      fromDate: '',
      toDate: ''
    });
    loadStatistics();
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'AED 0.00';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  const mappedDeliveryRows = useMemo(
    () =>
      (currentDeliveryNotes || []).map((note) => ({
        id: note.Id,
        refno: note.RefNo || '',
        date: formatNoteDate(note.Date),
        drivername: note.Driver?.Name || '',
        vehiclenumber: note.Vehicle?.Number || '',
        vehicledisplay: formatVehicleDisplay(note),
        totalorders: note.TotalOrders || 0,
        pending: note.Pending || 0,
        delivered: note.Delivered || 0,
        returned: note.Returned || 0,
        cancelled: note.Cancelled || 0,
        collectedamount: formatCurrency(note.CollectedAmount),
        pendingcollection: formatCurrency(note.PendingCollection),
        status: note.Status || '',
      })),
    [currentDeliveryNotes]
  );

  const groupedDeliveryRows = useMemo(
    () =>
      processClientRows({
        rows: mappedDeliveryRows,
        searchTerm: '',
        searchFields: [],
        filtersByField,
        sortField,
        sortDirection,
        groupField,
      }),
    [mappedDeliveryRows, filtersByField, sortField, sortDirection, groupField]
  );

  const flatDeliveryCount = useMemo(
    () => groupedDeliveryRows.reduce((sum, g) => sum + g.rows.length, 0),
    [groupedDeliveryRows]
  );

  const deliveryColSpan = DELIVERY_NOTE_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1;

  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => groupedDeliveryRows.filter((g) => g.label != null).map((g) => g.key),
    [groupedDeliveryRows]
  );

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = DELIVERY_NOTE_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    setFiltersByField((prev) => ({ ...prev, [field]: filter }));
  };

  const handleClearColumnFilter = (field) => {
    setFiltersByField((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const exportDeliveryTable = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Current Delivery Notes',
        fileName: `delivery_statistics_notes_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...DELIVERY_NOTE_COLUMNS.map((c) => c.title)],
        groups: groupedDeliveryRows,
        groupFieldTitle: DELIVERY_NOTE_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [
          serial,
          row.refno,
          row.date,
          row.drivername,
          row.vehicledisplay,
          row.totalorders,
          row.pending,
          row.delivered,
          row.returned,
          row.cancelled,
          row.collectedamount,
          row.pendingcollection,
          row.status,
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No delivery notes to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const renderDeliveryCell = (row, field) => {
    if (field === 'vehiclenumber') return row.vehicledisplay;
    return row[field] ?? '-';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'deliveryStatistics')}</h1>
            <p className="text-sm text-gray-500 mt-1">Comprehensive delivery performance metrics and analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showFilters
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={loadStatistics}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.fromDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.toDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, toDate: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:col-span-6 flex items-end gap-2">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex-1"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-1"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Delivery Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Delivery Notes</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.totalDeliveryNotes}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Confirmed */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Confirmed</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.confirmed}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.delivered}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Returned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Returned</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.returned}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Cancelled */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.cancelled}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* Processing */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processing</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.processing}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.totalOrders}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Package className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>

        {/* Collected Amount */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Collected Amount</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(statistics.collectedAmount)}</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Pending Collection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Collection</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(statistics.pendingCollection)}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Status Distribution</h2>
            <PieChart className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64">
            {statusData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">No data to display</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {statusData.map((entry) => (
                      <Cell key={`status-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Delivery Trend Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Delivery Trend</h2>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64">
            {trendData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">No data to display</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ReBarChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="orders" name="Orders" fill="#46955A" barSize={22} radius={[6, 6, 0, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Current Delivery Notes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Current Delivery Notes
            </h2>
            <div className="flex items-center gap-3">
              {flatDeliveryCount > 0 && (
                <span className="text-sm text-gray-600">{flatDeliveryCount} delivery notes</span>
              )}
              <button
                type="button"
                onClick={exportDeliveryTable}
                disabled={flatDeliveryCount === 0}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Export Excel"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${DELIVERY_NOTE_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setFiltersByField({});
                  setSortField('');
                  setSortDirection('asc');
                  setGroupField(null);
                }}
                className="text-primary hover:underline"
              >
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={DELIVERY_NOTE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={() => setGroupField(null)}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1400px] table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-[3%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">S.No</th>
                {DELIVERY_NOTE_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={DELIVERY_NOTE_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={setGroupField}
                      onUngroup={() => setGroupField(null)}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading && mappedDeliveryRows.length === 0 ? (
                <tr>
                  <td colSpan={deliveryColSpan} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Loading delivery notes...</p>
                  </td>
                </tr>
              ) : flatDeliveryCount === 0 ? (
                <tr>
                  <td colSpan={deliveryColSpan} className="px-6 py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-lg mb-2">No delivery notes found</p>
                    <p className="text-sm">Try adjusting your filters or refresh the page</p>
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return groupedDeliveryRows.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={deliveryColSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={DELIVERY_NOTE_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row) => {
                      serial += 1;
                      block.push(
                        <tr key={row.id || serial} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">{serial}</td>
                          {DELIVERY_NOTE_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`whitespace-nowrap px-4 py-2 text-sm text-gray-600 ${
                                  column.field === 'refno' ? 'font-medium text-gray-900' : ''
                                } ${['collectedamount', 'pendingcollection'].includes(column.field) ? 'font-medium' : ''}`}
                              >
                                {renderDeliveryCell(row, column.field)}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        {flatDeliveryCount > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            {flatDeliveryCount} delivery note(s)
            {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
            {groupField ? ` · grouped by ${DELIVERY_NOTE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}` : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryStatistics;

